import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { PageShell, Section } from '~/components/layout';
import { ArticleReadingLayout } from '~/components/sections/ArticleReadingLayout';
import { CtaBanner } from '~/components/sections/CtaBanner';
import { ButtonLink, Grid } from '~/components/ui';
import type { ArticleBlock, ArticleHeadingLevel, LearnArticle } from '~/content/learn';
import { cn } from '~/lib/cn';

/** The h1/intro block sits under this stable id so it anchors the quick-nav. */
const OVERVIEW_ID = 'overview';

/** 20px per outline step below h2 (h3 → 20px, h4 → 40px). */
const TOC_INSET_PX = 20;

/** Keep the reveal speed consistent while preventing extreme durations. */
const TOC_WAVE_MS_PER_PX = 5;
const TOC_WAVE_MIN_MS = 500;
const TOC_WAVE_MAX_MS = 2500;

interface Segment {
  id: string;
  /** `null` for the leading intro segment (the h1); otherwise the heading text. */
  heading: string | null;
  /** Outline level for TOC inset / body tag. Intro uses `null`. */
  level: ArticleHeadingLevel | null;
  blocks: Exclude<ArticleBlock, { type: 'heading' }>[];
}

/** Slugify a heading into an anchor id (matches the content module's rule). */
function slugifyHeading(value: string): string {
  return value
    .toLowerCase()
    .replace(/[’'"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Split a flat article body into segments: the leading intro (before the first
 * heading) plus one segment per heading. Each segment gets an `id` so it can be
 * a scroll target and a quick-nav anchor.
 */
function toSegments(body: LearnArticle['body']): Segment[] {
  const segments: Segment[] = [];
  const usedIds = new Map<string, number>([[OVERVIEW_ID, 1]]);
  let current: Segment = { id: OVERVIEW_ID, heading: null, level: null, blocks: [] };
  for (const block of body) {
    if (block.type === 'heading') {
      segments.push(current);
      const baseId = slugifyHeading(block.text) || 'section';
      const occurrence = (usedIds.get(baseId) ?? 0) + 1;
      usedIds.set(baseId, occurrence);
      current = {
        id: occurrence === 1 ? baseId : `${baseId}-${occurrence}`,
        heading: block.text,
        level: block.level ?? 2,
        blocks: [],
      };
    } else {
      current.blocks.push(block);
    }
  }
  segments.push(current);
  return segments;
}

/**
 * Tall/portrait frames (phone screenshots ≈ 9:16) are constrained and centered;
 * square and landscape stay edge-to-edge in the article column.
 */
const PORTRAIT_ASPECT_RATIO = 1.2;

function ArticleImage({ src, alt, caption }: { src: string; alt?: string; caption?: string }) {
  const [isPortrait, setIsPortrait] = useState<boolean | null>(null);

  const resolveLayout = (img: HTMLImageElement) => {
    if (!img.naturalWidth) return;
    const next = img.naturalHeight / img.naturalWidth > PORTRAIT_ASPECT_RATIO;
    setIsPortrait((prev) => (prev === next ? prev : next));
  };

  return (
    <figure>
      <img
        src={src}
        alt={alt ?? ''}
        loading="lazy"
        decoding="async"
        onLoad={(event) => resolveLayout(event.currentTarget)}
        ref={(img) => {
          // Cached images may already be complete before onLoad attaches.
          if (img?.complete) resolveLayout(img);
        }}
        className={cn(
          'mx-auto block h-auto',
          // Default to full width until measured so square/wide images do not flash small.
          // Avoid pairing max-w-full with max-w-sm — same property, CSS order is ambiguous.
          isPortrait === true ? 'w-auto max-w-sm' : 'w-full max-w-full',
        )}
      />
      {caption ? (
        <figcaption className="mt-3 text-body-s text-grey-500">{caption}</figcaption>
      ) : null}
    </figure>
  );
}

function ArticleTable({
  columns,
  rows,
  highlightLastRow,
}: {
  columns: string[];
  rows: string[][];
  highlightLastRow?: boolean;
}) {
  const columnCount = Math.max(columns.length, ...rows.map((row) => row.length));
  const normalizedColumns = [
    ...Array.from({ length: Math.max(0, columnCount - columns.length) }, () => ''),
    ...columns,
  ];

  return (
    <div className="w-full overflow-x-auto [-webkit-overflow-scrolling:touch]">
      <table className="w-full min-w-[320px] border-collapse overflow-hidden rounded-xl text-left text-body-s text-ink">
        <thead>
          <tr className="bg-red-700 text-white">
            {normalizedColumns.map((column, index) => (
              <th
                key={`${column}-${index}`}
                className={cn(
                  'px-5 py-3.5 font-semibold',
                  index === 0 ? 'text-white/60' : 'text-white',
                )}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const isTotal = Boolean(highlightLastRow && rowIndex === rows.length - 1);
            const isSectionRow = row.length === 1 && columnCount > 1;
            return (
              <tr
                key={`${row.join('|')}-${rowIndex}`}
                className={cn(
                  'border-b border-grey-100 bg-white last:border-b-0',
                  isTotal && 'bg-red-0',
                )}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    colSpan={isSectionRow ? columnCount : undefined}
                    className={cn(
                      'px-5 py-4 align-top',
                      isSectionRow && 'bg-grey-50 font-semibold text-ink',
                      cellIndex === row.length - 1 && row.length > 1
                        ? 'font-semibold text-red-700'
                        : 'text-ink',
                      isTotal && 'font-semibold',
                    )}>
                    {cell}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ArticlePromoCard({ block }: { block: Extract<ArticleBlock, { type: 'promoCard' }> }) {
  const isLight = block.tone === 'light';
  return (
    <aside
      className={cn(
        'rounded-[28px] p-6 md:p-8',
        block.tone === 'red' && 'bg-red-700 text-white',
        block.tone === 'dark' && 'bg-ink text-white',
        isLight && 'border border-grey-100 bg-grey-50 text-ink',
      )}>
      {block.eyebrow ? (
        <p
          className={cn(
            'text-[12px] font-semibold tracking-[0.12em] uppercase',
            isLight ? 'text-grey-500' : 'text-white/65',
          )}>
          {block.eyebrow}
        </p>
      ) : null}
      <p className={cn('text-h5 font-semibold tracking-[-0.02em]', block.eyebrow && 'mt-3')}>
        {block.title}
      </p>
      <p
        className={cn(
          'mt-4 whitespace-pre-line text-body',
          isLight ? 'text-grey-700' : 'text-white/80',
        )}>
        {block.body}
      </p>
      <ButtonLink
        href={block.ctaHref}
        external={block.openInNewTab || undefined}
        variant={isLight ? 'primary' : 'secondary'}
        className="mt-6">
        {block.ctaLabel}
      </ButtonLink>
    </aside>
  );
}

function ArticleBranchDirectory({
  block,
}: {
  block: Extract<ArticleBlock, { type: 'branchDirectory' }>;
}) {
  const [query, setQuery] = useState('');
  const [district, setDistrict] = useState('');
  const districts = Array.from(new Set(block.entries.map((entry) => entry.district))).sort((a, b) =>
    a.localeCompare(b),
  );
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filteredEntries = block.entries.filter((entry) => {
    if (district && entry.district !== district) return false;
    if (!normalizedQuery) return true;
    return [entry.branchName, entry.district, entry.routingNumber, entry.address ?? ''].some(
      (value) => value.toLocaleLowerCase().includes(normalizedQuery),
    );
  });
  const hasFilters = Boolean(query || district);

  return (
    <div className="rounded-[28px] border border-grey-100 bg-grey-50 p-5 md:p-7">
      <p className="text-h5 font-semibold tracking-[-0.02em] text-ink">{block.title}</p>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <label className="flex flex-col gap-2 text-body-s font-medium text-grey-700">
          Search branches
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={block.searchPlaceholder}
            className="h-12 w-full rounded-xl border border-grey-200 bg-white px-4 text-body text-ink outline-none placeholder:text-grey-400 focus:border-red-500"
          />
        </label>
        <label className="flex flex-col gap-2 text-body-s font-medium text-grey-700">
          {block.filterLabel}
          <select
            value={district}
            onChange={(event) => setDistrict(event.target.value)}
            className="h-12 w-full rounded-xl border border-grey-200 bg-white px-4 text-body text-ink outline-none focus:border-red-500">
            <option value="">All districts</option>
            {districts.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-5 flex min-h-8 items-center justify-between gap-4">
        <p className="text-body-s text-grey-500" aria-live="polite">
          {filteredEntries.length} {filteredEntries.length === 1 ? 'branch' : 'branches'}
        </p>
        {hasFilters ? (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setDistrict('');
            }}
            className="text-body-s font-medium text-red-500 hover:text-red-700">
            Clear filters
          </button>
        ) : null}
      </div>

      {filteredEntries.length > 0 ? (
        <div
          role="region"
          aria-label={`${block.title} results`}
          tabIndex={0}
          className="mt-3 max-h-[480px] overflow-auto overscroll-contain rounded-xl border border-grey-100 bg-white outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2">
          <table className="w-full min-w-[560px] border-collapse text-left text-body-s text-ink">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-grey-100 bg-white">
                <th className="px-4 py-3 font-semibold">Branch</th>
                <th className="px-4 py-3 font-semibold">District</th>
                <th className="px-4 py-3 font-semibold">Routing number</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry, index) => (
                <tr
                  key={`${entry.routingNumber}-${entry.branchName}-${index}`}
                  className="border-b border-grey-100 last:border-b-0">
                  <td className="px-4 py-3 align-top">
                    <span className="font-medium">{entry.branchName}</span>
                    {entry.address ? (
                      <span className="mt-1 block text-grey-500">{entry.address}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 align-top text-grey-700">{entry.district}</td>
                  <td className="px-4 py-3 align-top font-semibold text-red-700">
                    {entry.routingNumber}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-3 rounded-xl bg-white px-4 py-8 text-center text-body text-grey-500">
          {block.emptyMessage}
        </p>
      )}
    </div>
  );
}

const ARTICLE_CHART_COLORS = ['#9B1A1A', '#C9524A', '#E8A09D', '#6B7280', '#D6B1AE'];

function chartValue(
  block: Extract<ArticleBlock, { type: 'chart' }>,
  value: number,
  displayValue?: string,
) {
  if (displayValue) return displayValue;
  return `${block.valuePrefix ?? ''}${new Intl.NumberFormat('en', {
    maximumFractionDigits: 2,
  }).format(value)}${block.valueSuffix ?? ''}`;
}

function chartColor(
  dataset: Extract<ArticleBlock, { type: 'chart' }>['datasets'][number],
  datasetIndex: number,
  valueIndex: number,
) {
  return (
    dataset.colors?.[valueIndex] ??
    dataset.color ??
    ARTICLE_CHART_COLORS[(datasetIndex + valueIndex) % ARTICLE_CHART_COLORS.length]
  );
}

function shortChartLabel(label: string, maxLength = 18) {
  return label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label;
}

function ArticleCartesianChart({ block }: { block: Extract<ArticleBlock, { type: 'chart' }> }) {
  const width = 640;
  const height = block.horizontal ? Math.max(260, block.labels.length * 54 + 48) : 300;
  const padding = block.horizontal
    ? { top: 16, right: 32, bottom: 28, left: 128 }
    : { top: 22, right: 22, bottom: 56, left: 42 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const allValues = block.datasets.flatMap(({ values }) => values);
  const rawMin = Math.min(...allValues, 0);
  const rawMax = Math.max(...allValues, 0);
  const span = rawMax - rawMin || 1;
  const xForValue = (value: number) => padding.left + ((value - rawMin) / span) * plotWidth;
  const yForValue = (value: number) => padding.top + ((rawMax - value) / span) * plotHeight;

  if (block.chartType === 'line') {
    const xStep = block.labels.length > 1 ? plotWidth / (block.labels.length - 1) : 0;
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={block.title}
        className="h-auto w-full overflow-visible">
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + plotHeight * ratio;
          return (
            <line
              key={ratio}
              x1={padding.left}
              x2={width - padding.right}
              y1={y}
              y2={y}
              stroke="#ececec"
              strokeWidth="1"
            />
          );
        })}
        {block.datasets.map((dataset, datasetIndex) => {
          const points = dataset.values
            .map((value, index) => `${padding.left + xStep * index},${yForValue(value)}`)
            .join(' ');
          const color = chartColor(dataset, datasetIndex, 0);
          return (
            <g key={`${dataset.label ?? 'dataset'}-${datasetIndex}`}>
              <polyline
                points={points}
                fill="none"
                stroke={color}
                strokeWidth="3"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {dataset.values.map((value, index) => (
                <g key={index}>
                  <circle
                    cx={padding.left + xStep * index}
                    cy={yForValue(value)}
                    r={block.labels[index] === block.highlightedLabel ? 7 : 5}
                    fill={color}
                    stroke="white"
                    strokeWidth="3"
                  />
                  <title>{`${block.labels[index]}: ${chartValue(block, value, dataset.displayValues?.[index])}`}</title>
                </g>
              ))}
            </g>
          );
        })}
        {block.labels.map((label, index) => (
          <text
            key={label}
            x={padding.left + xStep * index}
            y={height - 18}
            textAnchor="middle"
            className="fill-grey-500 text-[11px]">
            {shortChartLabel(label, 10)}
          </text>
        ))}
      </svg>
    );
  }

  if (block.horizontal) {
    const groupHeight = plotHeight / block.labels.length;
    const barHeight = Math.min(28, (groupHeight * 0.72) / block.datasets.length);
    const zeroX = xForValue(0);
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={block.title}
        className="h-auto w-full overflow-visible">
        {block.labels.map((label, labelIndex) => (
          <g key={label}>
            <text
              x={padding.left - 12}
              y={padding.top + groupHeight * (labelIndex + 0.5) + 4}
              textAnchor="end"
              className={cn(
                'text-[12px]',
                label === block.highlightedLabel ? 'fill-red-700 font-semibold' : 'fill-grey-700',
              )}>
              {shortChartLabel(label)}
            </text>
            {block.datasets.map((dataset, datasetIndex) => {
              const value = dataset.values[labelIndex];
              const valueX = xForValue(value);
              const y =
                padding.top +
                groupHeight * labelIndex +
                (groupHeight - barHeight * block.datasets.length) / 2 +
                datasetIndex * barHeight;
              return (
                <g key={datasetIndex}>
                  <rect
                    x={Math.min(zeroX, valueX)}
                    y={y}
                    width={Math.max(2, Math.abs(valueX - zeroX))}
                    height={Math.max(8, barHeight - 4)}
                    rx="5"
                    fill={chartColor(dataset, datasetIndex, labelIndex)}
                  />
                  <text
                    x={valueX > width - padding.right - 72 ? valueX - 7 : valueX + 7}
                    y={y + barHeight / 2 + 1}
                    textAnchor={valueX > width - padding.right - 72 ? 'end' : 'start'}
                    dominantBaseline="middle"
                    className="fill-grey-700 text-[10px] font-semibold">
                    {chartValue(block, value, dataset.displayValues?.[labelIndex])}
                  </text>
                  <title>{`${label}: ${chartValue(block, value, dataset.displayValues?.[labelIndex])}`}</title>
                </g>
              );
            })}
          </g>
        ))}
      </svg>
    );
  }

  const groupWidth = plotWidth / block.labels.length;
  const barWidth = Math.min(52, (groupWidth * 0.72) / block.datasets.length);
  const zeroY = yForValue(0);
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={block.title}
      className="h-auto w-full overflow-visible">
      {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
        const y = padding.top + plotHeight * ratio;
        return (
          <line
            key={ratio}
            x1={padding.left}
            x2={width - padding.right}
            y1={y}
            y2={y}
            stroke="#ececec"
            strokeWidth="1"
          />
        );
      })}
      {block.labels.map((label, labelIndex) => (
        <g key={label}>
          {block.datasets.map((dataset, datasetIndex) => {
            const value = dataset.values[labelIndex];
            const valueY = yForValue(value);
            const x =
              padding.left +
              groupWidth * labelIndex +
              (groupWidth - barWidth * block.datasets.length) / 2 +
              datasetIndex * barWidth;
            return (
              <g key={datasetIndex}>
                <rect
                  x={x}
                  y={Math.min(zeroY, valueY)}
                  width={Math.max(8, barWidth - 4)}
                  height={Math.max(2, Math.abs(valueY - zeroY))}
                  rx="5"
                  fill={chartColor(dataset, datasetIndex, labelIndex)}
                />
                <text
                  x={x + Math.max(8, barWidth - 4) / 2}
                  y={Math.min(zeroY, valueY) - 7}
                  textAnchor="middle"
                  className="fill-grey-700 text-[10px] font-semibold">
                  {chartValue(block, value, dataset.displayValues?.[labelIndex])}
                </text>
                <title>{`${label}: ${chartValue(block, value, dataset.displayValues?.[labelIndex])}`}</title>
              </g>
            );
          })}
          <text
            x={padding.left + groupWidth * (labelIndex + 0.5)}
            y={height - 22}
            textAnchor="middle"
            className={cn(
              'text-[11px]',
              label === block.highlightedLabel ? 'fill-red-700 font-semibold' : 'fill-grey-500',
            )}>
            {shortChartLabel(label, 11)}
          </text>
        </g>
      ))}
    </svg>
  );
}

function ArticleDoughnutChart({ block }: { block: Extract<ArticleBlock, { type: 'chart' }> }) {
  const dataset = block.datasets[0];
  const total = dataset.values.reduce((sum, value) => sum + Math.max(0, value), 0) || 1;
  let offset = 0;
  const stops = dataset.values.map((value, index) => {
    const start = offset;
    offset += (Math.max(0, value) / total) * 100;
    return `${chartColor(dataset, 0, index)} ${start}% ${offset}%`;
  });

  return (
    <div className="grid items-center gap-7 sm:grid-cols-[220px_1fr]">
      <div
        role="img"
        aria-label={block.title}
        className="mx-auto aspect-square w-full max-w-[220px] rounded-full"
        style={{ background: `conic-gradient(${stops.join(', ')})` }}>
        <div className="m-[25%] aspect-square rounded-full bg-white" />
      </div>
      <dl className="grid gap-3">
        {block.labels.map((label, index) => (
          <div key={label} className="grid grid-cols-[12px_1fr_auto] items-center gap-3">
            <span
              aria-hidden
              className="size-3 rounded-sm"
              style={{ backgroundColor: chartColor(dataset, 0, index) }}
            />
            <dt className="text-body-s font-medium text-grey-700">{label}</dt>
            <dd className="text-body-s font-semibold text-ink">
              {chartValue(block, dataset.values[index], dataset.displayValues?.[index])}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function ArticleChart({ block }: { block: Extract<ArticleBlock, { type: 'chart' }> }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-grey-100 bg-white p-5 md:p-7">
      {block.eyebrow ? (
        <p className="text-[11px] font-semibold tracking-[0.08em] text-red-700 uppercase">
          {block.eyebrow}
        </p>
      ) : null}
      <h3 className={cn('text-body font-semibold text-ink', block.eyebrow && 'mt-1.5')}>
        {block.title}
      </h3>
      {block.subtitle ? <p className="mt-1 text-body-s text-grey-500">{block.subtitle}</p> : null}
      <div className="mt-6">
        {block.chartType === 'doughnut' ? (
          <ArticleDoughnutChart block={block} />
        ) : (
          <ArticleCartesianChart block={block} />
        )}
      </div>
      {block.source ? (
        <figcaption className="mt-5 border-t border-grey-100 pt-3 text-[11px] text-grey-400">
          {block.source}
        </figcaption>
      ) : null}
    </figure>
  );
}

function ArticleStatCards({ block }: { block: Extract<ArticleBlock, { type: 'statCards' }> }) {
  return (
    <aside className="rounded-[28px] border border-grey-100 bg-grey-50 p-6 md:p-8">
      {block.eyebrow ? (
        <p className="text-[11px] font-semibold tracking-[0.1em] text-red-700 uppercase">
          {block.eyebrow}
        </p>
      ) : null}
      {block.title ? (
        <h3
          className={cn(
            'text-h5 font-semibold tracking-[-0.02em] text-ink',
            block.eyebrow && 'mt-2',
          )}>
          {block.title}
        </h3>
      ) : null}
      {block.subtitle ? (
        <p className="mt-2 max-w-2xl text-body-s leading-[1.45] text-grey-500">{block.subtitle}</p>
      ) : null}
      <dl
        className={cn(
          'mt-7 grid gap-4',
          block.items.length === 1
            ? 'sm:grid-cols-1'
            : block.items.length === 3
              ? 'sm:grid-cols-3'
              : 'sm:grid-cols-2',
        )}>
        {block.items.map((item, index) => (
          <div
            key={`${item.label}-${index}`}
            className="flex min-h-40 flex-col rounded-2xl border border-grey-100 bg-white p-5 shadow-[0_1px_0_rgba(23,23,25,0.03)] md:p-6">
            {item.eyebrow ? (
              <dt className="text-[11px] font-semibold tracking-[0.08em] text-red-700 uppercase">
                {item.eyebrow}
              </dt>
            ) : null}
            <dd
              className={cn(
                'text-h4 leading-none font-semibold tracking-[-0.035em] text-ink',
                item.eyebrow && 'mt-2',
              )}>
              {item.value}
            </dd>
            <dt className="mt-auto max-w-sm pt-5 text-body-s leading-[1.4] font-medium text-grey-500">
              {item.label}
            </dt>
          </div>
        ))}
      </dl>
      {block.source ? (
        <p className="mt-5 border-t border-grey-100 pt-4 text-[11px] leading-[1.4] text-grey-400">
          {block.source}
        </p>
      ) : null}
    </aside>
  );
}

function ArticleVideo({ block }: { block: Extract<ArticleBlock, { type: 'video' }> }) {
  return (
    <figure>
      <div className="aspect-video overflow-hidden rounded-2xl bg-ink">
        <iframe
          src={block.src}
          title={block.title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="size-full border-0"
        />
      </div>
      {block.caption ? (
        <figcaption className="mt-3 text-body-s text-grey-500">{block.caption}</figcaption>
      ) : null}
    </figure>
  );
}

function ArticleVisualBlock({ children }: { children: ReactNode }) {
  return <div className="py-3 md:py-4">{children}</div>;
}

function Block({ block }: { block: Segment['blocks'][number] }) {
  switch (block.type) {
    case 'paragraph':
      return <p className="text-body leading-[1.58] text-grey-900">{block.text}</p>;
    case 'list':
      return (
        <ul className="flex list-disc flex-col gap-2.5 pl-5 text-body leading-[1.52] text-grey-900 marker:text-grey-400">
          {block.items.map((item) => (
            <li key={item} className="pl-1">
              {item}
            </li>
          ))}
        </ul>
      );
    case 'image':
      return (
        <ArticleVisualBlock>
          <ArticleImage src={block.src} alt={block.alt} caption={block.caption} />
        </ArticleVisualBlock>
      );
    case 'table':
      return (
        <ArticleVisualBlock>
          <ArticleTable
            columns={block.columns}
            rows={block.rows}
            highlightLastRow={block.highlightLastRow}
          />
        </ArticleVisualBlock>
      );
    case 'promoCard':
      return (
        <ArticleVisualBlock>
          <ArticlePromoCard block={block} />
        </ArticleVisualBlock>
      );
    case 'branchDirectory':
      return (
        <ArticleVisualBlock>
          <ArticleBranchDirectory block={block} />
        </ArticleVisualBlock>
      );
    case 'chart':
      return (
        <ArticleVisualBlock>
          <ArticleChart block={block} />
        </ArticleVisualBlock>
      );
    case 'statCards':
      return (
        <ArticleVisualBlock>
          <ArticleStatCards block={block} />
        </ArticleVisualBlock>
      );
    case 'video':
      return (
        <ArticleVisualBlock>
          <ArticleVideo block={block} />
        </ArticleVisualBlock>
      );
  }
}

function SectionHeading({ level, children }: { level: ArticleHeadingLevel; children: string }) {
  if (level === 3) {
    return <h3 className="text-h5 font-semibold tracking-[-0.02em] text-ink">{children}</h3>;
  }
  if (level === 4) {
    return <h4 className="text-body font-semibold tracking-[-0.01em] text-ink">{children}</h4>;
  }
  return <h2 className="text-h5 font-semibold tracking-[-0.02em] md:text-h4">{children}</h2>;
}

export function LearnArticleDetail({ article }: { article: LearnArticle }) {
  const segments = toSegments(article.body);
  const [intro, ...sections] = segments;
  const tocItems = segments.map((segment) => ({
    id: segment.id,
    label: segment.heading ?? article.title,
    /** Intro + h2 stay flush; h3/h4 nest by 20px steps. */
    insetPx: segment.level && segment.level > 2 ? (segment.level - 2) * TOC_INSET_PX : 0,
  }));

  const [activeId, setActiveId] = useState(OVERVIEW_ID);
  const tocRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const toc = tocRef.current;
    if (!toc) return;

    const duration = Math.round(
      Math.min(TOC_WAVE_MAX_MS, Math.max(TOC_WAVE_MIN_MS, toc.scrollHeight * TOC_WAVE_MS_PER_PX)),
    );
    toc.style.setProperty('--toc-wave-duration', `${duration}ms`);
  }, [article.fullPath, tocItems.length]);

  // Scroll-spy: mark the section nearest the top of the viewport as active so
  // the quick-nav tracks where the reader is. The rootMargin biases towards the
  // section just below the sticky header. Targets are read from the DOM.
  useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>('[data-toc-target]'));
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length === 0) return;
        const topmost = visible.reduce((a, b) =>
          a.boundingClientRect.top <= b.boundingClientRect.top ? a : b,
        );
        setActiveId(topmost.target.id);
      },
      { rootMargin: '-96px 0px -65% 0px', threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleTocClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  };

  const avatar = article.author.avatar;

  return (
    <PageShell>
      <Section className="pt-10 pb-36 md:pt-14 md:pb-44">
        <Grid>
          <ArticleReadingLayout
            toc={
              <nav
                ref={tocRef}
                aria-label="On this page"
                className="toc-wave-in group/toc flex flex-col gap-0.5 xl:mt-[52px]">
                {tocItems.map((item) => {
                  const isActive = item.id === activeId;
                  return (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      aria-current={isActive ? 'true' : undefined}
                      onClick={(event) => handleTocClick(event, item.id)}
                      style={{
                        paddingLeft: item.insetPx ? `${item.insetPx}px` : undefined,
                      }}
                      className={cn(
                        'py-1.5 pr-3 text-body-s leading-[1.35] transition-colors ease-nav duration-150 group-has-[a:hover]/toc:text-grey-400 hover:!text-grey-1000',
                        isActive ? 'font-medium text-grey-1000' : 'text-grey-500',
                      )}>
                      {item.label}
                    </a>
                  );
                })}
              </nav>
            }>
            <Link
              to="/help"
              className="group inline-flex items-center gap-2 text-body-s font-medium text-red-500 transition-opacity duration-150 ease-nav hover:opacity-70">
              <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden="true">
                <path
                  d="M7.68115 18.5L2.86084 12.5674L2.3999 12L2.86084 11.4326L7.68115 5.5L10.0005 5.5L4.71924 12L10.0005 18.5L7.68115 18.5Z"
                  fill="currentColor"
                />
                <path d="M4 12H21.5" stroke="currentColor" strokeWidth={1.8} />
              </svg>
              <span className="link-underline-lr bg-origin-content">Back</span>
            </Link>

            <section id={intro.id} data-toc-target className="mt-7 scroll-mt-24">
              <h1 className="text-h3 leading-[1.08] font-semibold tracking-[-0.035em] text-balance md:text-[42px]">
                {article.title}
              </h1>
              <div className="mt-7 flex items-center gap-3.5">
                {avatar ? (
                  <img
                    src={avatar}
                    alt={article.author.name}
                    draggable={false}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-grey-100 text-body-s font-medium text-grey-700">
                    {article.author.name.charAt(0)}
                  </span>
                )}
                <div className="flex flex-col gap-1">
                  <span className="font-medium text-ink">{article.author.name}</span>
                  <span className="text-body-s font-medium text-grey-500">{article.date}</span>
                </div>
              </div>
              {intro.blocks.length > 0 && (
                <div className="mt-9 space-y-5">
                  {intro.blocks.map((block, index) => (
                    <Block key={index} block={block} />
                  ))}
                </div>
              )}
            </section>

            <div className="mt-18">
              {sections.map((segment, index) => {
                const level = segment.level ?? 2;
                const isNested = level >= 3;
                return (
                  <section
                    key={segment.id}
                    id={segment.id}
                    data-toc-target
                    className={cn('scroll-mt-24', index > 0 && (isNested ? 'mt-9' : 'mt-16'))}>
                    <SectionHeading level={level}>{segment.heading ?? ''}</SectionHeading>
                    {segment.blocks.length > 0 && (
                      <div className={cn('space-y-5', isNested ? 'mt-5' : 'mt-7')}>
                        {segment.blocks.map((block, blockIndex) => (
                          <Block key={blockIndex} block={block} />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </ArticleReadingLayout>
        </Grid>
      </Section>
      <CtaBanner />
    </PageShell>
  );
}
